import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Check, Lock, ScanFace } from 'lucide-react'
import { appointmentsAPI, authAPI } from '../services/api'
import { useUserStore } from '../stores'
import { useI18n } from '../i18n'
import { OverlayPortal } from './ModalPortal'
import { biometricAvailable, verifyBiometric } from '../utils/biometric'
import { identityOffer, signOnBody, stateName, VIDEO_CONSENT_POINTS } from '../utils/videoSignOn'
import { OrellaSpinner } from './OrellaLoader' // s123: the app's one spinner

const NAVY = '#15354F'
const BLUE = '#1E6FB8'

/**
 * VideoSignOnFlow — s114 (canvas "Orella Video Visit Sign-On & Consent" V2 + V3, approved 2026-10-22).
 * Runs after the check-in of a VIDEO visit, before the Join screen. Step 1 "Before your video visit": who (signed in
 * as; Face ID or PIN when the phone / account has one) and where (one of the physician's license states). Step 2:
 * the video-visit consent (every visit). Then POST /appointments/<id>/video-sign-on and onDone(record).
 */
export default function VideoSignOnFlow({ appointmentId, doctorName = '', patientName = '', onDone, onCancel }) {
  const { t } = useI18n()
  const pinEnabled = useUserStore((s) => !!s.user?.sessionPinEnabled)
  const logout = useUserStore((s) => s.logout)
  const navigate = useNavigate()
  const [step, setStep] = useState('before')
  const [info, setInfo] = useState(null)          // { licensedStates }
  const [infoFailed, setInfoFailed] = useState(false)
  const [offer, setOffer] = useState(null)        // 'faceid' | 'pin' | 'none'
  const [confirmed, setConfirmed] = useState(null) // 'faceid' | 'pin' | null
  const [pinMode, setPinMode] = useState(false)
  const [pin, setPin] = useState('')
  const [pinError, setPinError] = useState('')
  const [pinBusy, setPinBusy] = useState(false)
  const [where, setWhere] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let alive = true
    appointmentsAPI.videoSignOnInfo(appointmentId)
      .then((r) => {
        if (!alive) return
        const states = Array.isArray(r?.licensedStates) ? r.licensedStates : []
        setInfo({ licensedStates: states })
        if (states.length === 1) setWhere(states[0])
      })
      .catch(() => { if (alive) setInfoFailed(true) })
    biometricAvailable().then((bio) => { if (alive) setOffer(identityOffer({ biometricAvailable: bio, pinEnabled })) })
    return () => { alive = false }
  }, [appointmentId, pinEnabled])

  const states = info?.licensedStates || []
  const primary = states.length ? stateName(states[0]) : ''
  const identityOk = offer === 'none' || !!confirmed
  const ready = identityOk && !!where && where !== 'other'

  const confirmFaceId = async () => {
    const ok = await verifyBiometric(t('videoSignOn.confirmPrompt'))
    if (ok) setConfirmed('faceid')
    else if (pinEnabled) setPinMode(true)
  }
  const submitPin = async (value) => {
    if (pinBusy || value.length !== 4) return
    setPinBusy(true); setPinError('')
    try {
      const r = await authAPI.verifySessionPin(value)
      if (r?.valid) { setConfirmed('pin'); setPinMode(false) } else { setPinError(t('lockScreen.incorrectPin')); setPin('') }
    } catch {
      setPinError(t('lockScreen.couldNotVerify')); setPin('')
    } finally {
      setPinBusy(false)
    }
  }
  const finish = async () => {
    const body = signOnBody({ method: confirmed || 'app_sign_in', where, agreed })
    if (!body || saving) return
    setSaving(true); setError('')
    try {
      const r = await appointmentsAPI.videoSignOn(appointmentId, body)
      onDone && onDone(r?.videoSignOn || null)
    } catch (e) {
      if (e?.code === 'location_not_licensed' || e?.data?.code === 'location_not_licensed') { setStep('before'); setWhere('other') }
      setError(t('errors2.somethingWentWrong'))
    } finally {
      setSaving(false)
    }
  }

  const choice = (on) => ({
    display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '0 16px', borderRadius: 14, width: '100%',
    fontSize: 15, fontWeight: 700, color: NAVY, textAlign: 'left',
    border: on ? `2px solid ${BLUE}` : '1px solid #DCE6EE', background: on ? '#E9F8FF' : '#fff',
  })
  const dot = (on) => ({ width: 18, height: 18, borderRadius: '50%', flex: 'none', boxSizing: 'border-box',
    border: on ? `6px solid ${BLUE}` : '2px solid #94A3B8' })

  return (
    <OverlayPortal>
    <div data-video-signon className="fixed inset-0 z-[10000] bg-white flex flex-col" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}>
      <div className="px-4 flex items-center gap-2">
        <button type="button" aria-label={t('common.back', 'Back')} className="w-11 h-11 flex items-center justify-center"
          onClick={() => (step === 'consent' ? setStep('before') : onCancel && onCancel())}>
          <ArrowLeft size={22} color={NAVY} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        {step === 'before' && (
          <>
            <h2 className="text-[24px] font-extrabold leading-tight" style={{ color: NAVY }}>{t('videoSignOn.title')}</h2>
            <div className="rounded-2xl border border-[#DCE6EE] px-4 py-3.5 flex flex-col gap-3">
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <p className="text-[12px] font-bold tracking-wide uppercase text-[#5F7A8E]">{t('videoSignOn.signedInAs')}</p>
                  <p className="text-[16px] font-extrabold" style={{ color: NAVY }}>{patientName}</p>
                </div>
                <button type="button" data-signon-signout className="text-[13px] font-bold" style={{ color: BLUE }}
                  onClick={() => { logout(); navigate('/signin', { replace: true }) }}>
                  {t('videoSignOn.notYou')}
                </button>
              </div>
              {offer && offer !== 'none' && !confirmed && !pinMode && (
                <div className="flex flex-col gap-2">
                  <p className="text-[14px] text-[#3B5568]">{t('videoSignOn.confirmPrompt')}</p>
                  {offer === 'faceid' && (
                    <button type="button" data-signon-faceid onClick={confirmFaceId}
                      className="h-12 rounded-xl text-white text-[15px] font-extrabold flex items-center justify-center gap-2" style={{ background: BLUE }}>
                      <ScanFace size={20} /> {t('videoSignOn.faceIdButton')}
                    </button>
                  )}
                  {pinEnabled && (
                    <button type="button" data-signon-pin onClick={() => setPinMode(true)}
                      className={offer === 'faceid' ? 'h-10 text-[14px] font-bold' : 'h-12 rounded-xl text-white text-[15px] font-extrabold flex items-center justify-center gap-2'}
                      style={offer === 'faceid' ? { color: BLUE } : { background: BLUE }}>
                      {offer === 'faceid' ? t('videoSignOn.usePin') : <><Lock size={18} /> {t('lockScreen.enterPin')}</>}
                    </button>
                  )}
                </div>
              )}
              {pinMode && !confirmed && (
                <div className="flex flex-col gap-2">
                  <label className="text-[13px] font-bold text-[#3B5568]" htmlFor="signon-pin">{t('lockScreen.enterPin')}</label>
                  <input id="signon-pin" data-signon-pin-input type="password" inputMode="numeric" autoComplete="off" maxLength={4}
                    value={pin} disabled={pinBusy}
                    onChange={(e) => { const v = e.target.value.replace(/\D/g, '').slice(0, 4); setPin(v); if (v.length === 4) submitPin(v) }}
                    className="h-12 rounded-xl border border-[#CBD5E1] px-4 text-[20px] tracking-[0.5em] text-center" />
                  {pinError && <p className="text-[13px] text-[#B42318]">{pinError}</p>}
                </div>
              )}
              {confirmed && (
                <p data-signon-confirmed className="flex items-center gap-2 text-[14px] font-extrabold text-[#166534]">
                  <Check size={16} strokeWidth={3} /> {confirmed === 'faceid' ? t('videoSignOn.confirmedFaceId') : t('videoSignOn.confirmedPin')}
                </p>
              )}
            </div>

            <div className="flex flex-col gap-2.5">
              <h3 className="text-[18px] font-extrabold" style={{ color: NAVY }}>{t('videoSignOn.whereNow')}</h3>
              {!info && !infoFailed && <OrellaSpinner size={20} />}
              {infoFailed && <p className="text-[14px] text-[#B42318]">{t('errors2.somethingWentWrong')}</p>}
              {info && (
                <>
                  <p className="text-[14px] text-[#3B5568]">{t('videoSignOn.explainer', { doctor: doctorName, state: primary })}</p>
                  {states.map((c) => (
                    <button key={c} type="button" data-signon-state={c} style={choice(where === c)} onClick={() => setWhere(c)}>
                      <span style={dot(where === c)} />{t('videoSignOn.inState', { state: stateName(c) })}
                    </button>
                  ))}
                  <button type="button" data-signon-state="other" style={choice(where === 'other')} onClick={() => setWhere('other')}>
                    <span style={dot(where === 'other')} />{t('videoSignOn.otherState')}
                  </button>
                  {where === 'other' && (
                    <div data-signon-other className="rounded-2xl border border-[#F5C58A] bg-[#FFF8EE] px-4 py-3.5">
                      <p className="text-[14px] leading-relaxed text-[#5A3A0B]">{t('videoSignOn.otherStateMsg', { state: primary })}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        {step === 'consent' && (
          <>
            <h2 className="text-[24px] font-extrabold leading-tight" style={{ color: NAVY }}>{t('videoSignOn.consentTitle')}</h2>
            <div className="rounded-2xl border border-[#DCE6EE] p-4 flex flex-col gap-3">
              {VIDEO_CONSENT_POINTS.map((k) => (
                <div key={k} className="flex gap-2.5 text-[14.5px] leading-relaxed text-[#26445A]">
                  <span className="w-1.5 h-1.5 rounded-full flex-none mt-[9px]" style={{ background: BLUE }} />
                  <span>{t(`videoSignOn.${k}`, { doctor: doctorName })}</span>
                </div>
              ))}
            </div>
            <label className="flex items-start gap-3 py-3 px-1 text-[15px] font-bold leading-snug" style={{ color: NAVY }}>
              <input type="checkbox" data-signon-agree checked={agreed} onChange={() => setAgreed(!agreed)} className="w-[22px] h-[22px] flex-none" />
              <span>{t('videoSignOn.consentCheckbox')}</span>
            </label>
            <p className="text-[12.5px] text-center text-[#5F7A8E]">{t('videoSignOn.consentFootnote')}</p>
          </>
        )}
        {error && <p className="text-[14px] text-[#B42318]">{error}</p>}
      </div>
      <div className="px-4 pt-2" style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}>
        {step === 'before' ? (
          <button type="button" data-signon-continue disabled={!ready} onClick={() => setStep('consent')}
            className="w-full h-[52px] rounded-2xl text-[16px] font-extrabold"
            style={ready ? { background: NAVY, color: '#fff' } : { background: '#E2E8F0', color: '#5F7A8E' }}>
            {t('videoSignOn.continue')}
          </button>
        ) : (
          <button type="button" data-signon-agree-continue disabled={!agreed || saving} onClick={finish}
            className="w-full h-[52px] rounded-2xl text-[16px] font-extrabold flex items-center justify-center gap-2"
            style={agreed && !saving ? { background: NAVY, color: '#fff' } : { background: '#E2E8F0', color: '#5F7A8E' }}>
            {saving && <OrellaSpinner size={18} tone="inherit" />}{t('videoSignOn.agreeContinue')}
          </button>
        )}
      </div>
    </div>
    </OverlayPortal>
  )
}
